/**
 * The Translations section of a form (P6-13), bound to React Hook Form: `translations` of a record in Hindi, Marathi
 * and Gujarati (`TranslationsEditor` from the UI kit). The API validates and stores them (apps.core.i18n).
 */

import { useContext } from "react";
import { Controller, type Control, type FieldPath, type FieldValues } from "react-hook-form";

import { useTranslation } from "@motolynk/i18n";
import { TranslationsEditor, type TranslatableFieldDef, type Translations } from "@motolynk/ui";

import { useRichTextLabels } from "./labels";
import { PanelContext } from "./session";

/** The content languages besides English (each can be switched off: `storefront.language.<code>`). */
export const CONTENT_LANGUAGES = [
  { code: "hi", label: "हिन्दी" },
  { code: "mr", label: "मराठी" },
  { code: "gu", label: "ગુજરાતી" },
];

export type { Translations };

/**
 * The content languages the admin currently offers. A language switched off in Feature flags has no tabs (its saved
 * texts stay untouched and come back when it is switched on again); a missing flag counts as on.
 */
export function useContentLanguages(): typeof CONTENT_LANGUAGES {
  const features = useContext(PanelContext)?.features ?? {};
  return CONTENT_LANGUAGES.filter(
    (language) => features[`storefront.language.${language.code}`] !== false,
  );
}

/** Pull `translations.<lang>.<field>` messages out of an API error for the editor. */
export function translationErrors(
  errors: Record<string, string[] | undefined> | undefined,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [path, messages] of Object.entries(errors ?? {})) {
    if (path.startsWith("translations") && messages?.[0]) out[path] = messages[0];
  }
  return out;
}

export function TranslationsField<T extends FieldValues>({
  control,
  name = "translations" as FieldPath<T>,
  fields,
  errors,
  disabled,
}: {
  control: Control<T>;
  name?: FieldPath<T>;
  fields: TranslatableFieldDef[];
  errors?: Record<string, string>;
  disabled?: boolean;
}) {
  const { t } = useTranslation("common");
  const richTextLabels = useRichTextLabels();
  const languages = useContentLanguages();
  if (languages.length === 0) return null; // English only: nothing to translate
  return (
    <Controller
      control={control}
      name={name}
      render={({ field }) => (
        <TranslationsEditor
          title={t("translations.title")}
          description={t("translations.description")}
          languages={languages}
          fields={fields}
          value={(field.value as Translations | undefined) ?? {}}
          onChange={field.onChange}
          richTextLabels={richTextLabels}
          errors={errors}
          disabled={disabled}
        />
      )}
    />
  );
}
